import { useEffect, useRef } from 'react';

import { createSlug } from '../../util/urlHelpers';
import {
  loadInventoryContext,
  saveInventoryContext,
  saveInventoryScroll,
} from '../../util/inventoryNavigation';

/**
 * Records the search results shown on this page, so a vehicle page can link back here with
 * previous and next vehicles, and restores the scroll position when the seller comes back.
 *
 * Renders nothing. Filters, sorting and the page number live in the URL, so returning to the
 * same URL restores them without any extra state here.
 */
const InventoryContextRecorder = props => {
  const { location, listings, listingsAreLoaded } = props;
  const returnTo = `${location.pathname}${location.search}`;

  // Scroll is only recorded after any saved position has been restored, so the first scroll
  // event on a fresh page cannot overwrite it with zero.
  const readyToRecordScroll = useRef(false);

  useEffect(() => {
    readyToRecordScroll.current = false;
  }, [returnTo]);

  useEffect(() => {
    if (!listingsAreLoaded) return;
    const items = (listings || []).map(listing => ({
      id: listing.id.uuid,
      slug: createSlug(listing.attributes.title || ''),
    }));
    saveInventoryContext({ returnTo, items });

    if (!readyToRecordScroll.current) {
      const saved = loadInventoryContext();
      if (saved && saved.returnTo === returnTo && saved.scrollY > 0) {
        window.scrollTo(0, saved.scrollY);
      }
      readyToRecordScroll.current = true;
    }
  }, [listings, listingsAreLoaded, returnTo]);

  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (!readyToRecordScroll.current || frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        saveInventoryScroll(returnTo, window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [returnTo]);

  return null;
};

export default InventoryContextRecorder;
